import { ReactNode } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Colors, ModernPalette, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';

interface SettingSectionProps {
  title?: string;
  children: ReactNode;
  textColor: string;
}

/**
 * Setting Section Component
 * Groups related settings under a titled section header.
 * Provides consistent spacing and styling across settings groups.
 * When `title` is omitted, renders just the content card.
 */
export function SettingSection({ title, children, textColor }: SettingSectionProps) {
  const colorScheme = useColorScheme();
  const backgroundColor = colorScheme === 'light' ? ModernPalette.white : Colors[colorScheme ?? 'light'].backgroundLight;

  return (
    <View style={styles.container}>
      {title && (
        <Text style={[styles.title, { color: textColor, fontFamily: getBodyFont() }]}>{title}</Text>
      )}
      <View style={[styles.content, { backgroundColor }]}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: Spacing.xxl,
  },
  title: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    opacity: 0.6,
    marginBottom: Spacing.sm,
    paddingHorizontal: Spacing.lg,
  },
  content: {
    borderRadius: BorderRadius.md,
    overflow: 'hidden',
    ...Shadows.sm,
  },
});
